/**
 * Profile image editing (ADR-0019). Everything happens in the browser: the
 * original is normalised (orientation applied, EXIF/location stripped, at most
 * 2048 px), the editor state (`Crop`) is applied on a canvas, and both the
 * rendering and the original are uploaded so the user can re-edit later.
 */
import type { Crop } from "./types";

export type ImageKind = "avatar" | "cover";

/** Output sizes: a square photo, and a 4:1 background like LinkedIn's. */
export const OUTPUT: Record<ImageKind, { w: number; h: number }> = {
  avatar: { w: 400, h: 400 },
  cover: { w: 1584, h: 396 },
};

export const DEFAULT_CROP: Crop = { zoom: 1, x: 0, y: 0, rotation: 0, brightness: 1, contrast: 1, saturation: 1 };

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("This image can't be opened here. Try a JPEG or PNG."));
    img.src = src;
  });
}

function toBlob(canvas: HTMLCanvasElement, quality = 0.9): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Couldn't prepare the image"))), "image/jpeg", quality));
}

/** Re-encode the picked file: upright, no metadata, longest side ≤ 2048 px. */
export async function normalizeSource(file: Blob): Promise<{ blob: Blob; url: string }> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const scale = Math.min(1, 2048 / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#fff"; // transparent PNGs become white, not black
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await toBlob(canvas, 0.92);
    return { blob, url: URL.createObjectURL(blob) };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Where the image sits inside a frame of `fw`×`fh`: its drawn (unrotated) size
 * and the offset of its centre from the frame's centre. Offsets are stored as
 * fractions of the frame, so the same crop renders identically at any size.
 * The image always covers the frame; `clamped` is the crop with offsets kept
 * inside that range.
 */
export function place(fw: number, fh: number, iw: number, ih: number, crop: Crop) {
  const turned = crop.rotation % 180 !== 0;
  const rw = turned ? ih : iw;
  const rh = turned ? iw : ih;
  const scale = Math.max(fw / rw, fh / rh) * crop.zoom;
  const maxX = Math.max(0, (rw * scale - fw) / 2);
  const maxY = Math.max(0, (rh * scale - fh) / 2);
  const ox = Math.max(-maxX, Math.min(maxX, crop.x * fw));
  const oy = Math.max(-maxY, Math.min(maxY, crop.y * fh));
  return { w: iw * scale, h: ih * scale, ox, oy, clamped: { ...crop, x: fw ? ox / fw : 0, y: fh ? oy / fh : 0 } };
}

/** CSS filter for the live preview; `render` applies the same maths to pixels. */
export function cssFilter(c: Crop): string {
  return `brightness(${c.brightness}) contrast(${c.contrast}) saturate(${c.saturation})`;
}

function adjust(ctx: CanvasRenderingContext2D, w: number, h: number, c: Crop) {
  if (c.brightness === 1 && c.contrast === 1 && c.saturation === 1) return;
  const data = ctx.getImageData(0, 0, w, h);
  const px = data.data;
  for (let i = 0; i < px.length; i += 4) {
    let r = px[i] * c.brightness, g = px[i + 1] * c.brightness, b = px[i + 2] * c.brightness;
    r = (r - 127.5) * c.contrast + 127.5;
    g = (g - 127.5) * c.contrast + 127.5;
    b = (b - 127.5) * c.contrast + 127.5;
    const l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    px[i] = l + (r - l) * c.saturation;
    px[i + 1] = l + (g - l) * c.saturation;
    px[i + 2] = l + (b - l) * c.saturation;
  }
  ctx.putImageData(data, 0, 0);
}

/** Draw the final photo or background from the original and the editor state. */
export async function render(img: HTMLImageElement, kind: ImageKind, crop: Crop): Promise<Blob> {
  const { w: W, h: H } = OUTPUT[kind];
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingQuality = "high";
  const p = place(W, H, img.naturalWidth, img.naturalHeight, crop);
  ctx.translate(W / 2 + p.ox, H / 2 + p.oy);
  ctx.rotate((crop.rotation * Math.PI) / 180);
  ctx.drawImage(img, -p.w / 2, -p.h / 2, p.w, p.h);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  adjust(ctx, W, H, crop);
  return toBlob(canvas, 0.9);
}
